import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { normalizeLocalType, dashboardRouteFromType } from "../../utils/userTypes";

/**
 * Guarda de rota por tipo de usuário para os dashboards.
 *
 * O ProtectedRoute só garante que existe sessão (autenticação), não que o
 * usuário é do tipo certo (autorização). Sem esta guarda, uma família que
 * abrisse/atualizasse /dashboard-baba veria o dashboard da babá — e vice-versa.
 *
 * A fonte da verdade é o banco (usuarios.tipo / is_admin), não o localStorage
 * `user_type`, que pode estar ausente ou desatualizado. Quando o tipo real não
 * bate com o esperado, redireciona para o dashboard do próprio tipo.
 *
 * @param {"family"|"babysitter"|"admin"} allow tipo permitido nesta rota
 */
export default function RequireUserType({ allow, children }) {
  const [state, setState] = useState({ status: "loading", redirect: null });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        if (!cancelled) setState({ status: "redirect", redirect: "/login" });
        return;
      }

      const { data: usuario } = await supabase
        .from("usuarios")
        .select("tipo, is_admin")
        .eq("id", session.user.id)
        .maybeSingle();

      if (cancelled) return;

      const tipo = usuario?.is_admin ? "admin" : normalizeLocalType(usuario?.tipo);

      if (!tipo) {
        // Sem tipo resolvível: força re-autenticação em vez de arriscar loop.
        setState({ status: "redirect", redirect: "/login" });
        return;
      }

      if (tipo === allow) {
        setState({ status: "ok", redirect: null });
      } else {
        setState({ status: "redirect", redirect: dashboardRouteFromType(tipo) });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [allow]);

  if (state.status === "loading") return null;
  if (state.status === "redirect") return <Navigate to={state.redirect} replace />;
  return children;
}
